import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  MenuItem,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  useTheme,
  Divider,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Collapse,
  Fade,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';

// Icons
import ReceiptIcon from '@mui/icons-material/Receipt';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import DateRangeIcon from '@mui/icons-material/DateRange';

const EXPENSE_CATEGORIES = [
  'Office Supplies',
  'Equipment',
  'Travel',
  'Meals',
  'Utilities',
  'Maintenance',
  'Rent',
  'Insurance',
  'Software',
  'Marketing',
  'Other',
];

interface ExpenseData {
  id: string;
  category: string;
  amount: number;
  description: string;
  created_at: string;
}

export function Expenses() {
  const { user } = useAuth();
  const theme = useTheme();
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentExpenses, setRecentExpenses] = useState<ExpenseData[]>([]);
  const [fetchingExpenses, setFetchingExpenses] = useState(true);
  const [totalExpenses, setTotalExpenses] = useState(0);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  useEffect(() => {
    if (user) {
      fetchRecentExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchRecentExpenses = async () => {
    if (!user) return;
    
    try {
      setFetchingExpenses(true);
      
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .eq('employee_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setRecentExpenses(data || []);
      
      const total = (data || []).reduce((sum, expense) => sum + expense.amount, 0);
      setTotalExpenses(total);
      
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Failed to load recent expenses');
    } finally {
      setFetchingExpenses(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      // Insert to Supabase
      const newExpense = {
        employee_id: user.id,
        category,
        amount: parseFloat(amount),
        description,
        date: new Date().toISOString()
      };

      const { data, error: insertError } = await supabase
        .from('expenses')
        .insert(newExpense)
        .select()
        .single();
        
      if (insertError) throw insertError;

      setSuccess('Expense recorded successfully');
      
      // Add new expense to recent expenses list
      setRecentExpenses(prev => [data, ...prev.slice(0, 9)]);
      setTotalExpenses(prev => prev + parseFloat(amount));
      
      setCategory('');
      setAmount('');
      setDescription('');
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      
    } catch (error) {
      console.error('Error creating expense:', error);
      setError('Failed to record expense');
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = useMemo(() => {
    return recentExpenses.filter(expense => {
      const matchCat = filterCategory ? expense.category === filterCategory : true;
      const matchMin = minAmountFilter ? expense.amount >= Number(minAmountFilter) : true;
      const matchMax = maxAmountFilter ? expense.amount <= Number(maxAmountFilter) : true;
      let matchStart = true;
      let matchEnd = true;
      if (startDateFilter) {
        matchStart = new Date(expense.created_at) >= new Date(startDateFilter);
      }
      if (endDateFilter) {
        const end = new Date(endDateFilter);
        end.setDate(end.getDate() + 1);
        matchEnd = new Date(expense.created_at) < end;
      }
      return matchCat && matchMin && matchMax && matchStart && matchEnd;
    });
  }, [recentExpenses, filterCategory, minAmountFilter, maxAmountFilter, startDateFilter, endDateFilter]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Grid container spacing={3}>
      {/* Left side - Expense Form */}
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(to right bottom, #1f2937, #111827)'
              : 'linear-gradient(to right bottom, #f9fafb, #f3f4f6)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              Record Expense
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Track business expenses by filling out the form below.
          </Typography>
        </Paper>

        {error && (
          <Collapse in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Collapse>
        )}

        {success && (
          <Fade in={!!success}>
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              icon={<CheckCircleIcon fontSize="inherit" />}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          </Fade>
        )}

        <Card variant="outlined" sx={{ mb: { xs: 3, md: 0 } }}>
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="category-select-label">Category</InputLabel>
                    <Select
                      labelId="category-select-label"
                      id="category-select"
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <CategoryIcon />
                        </InputAdornment>
                      }
                    >
                      {EXPENSE_CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select the type of expense</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Enter the expense amount"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter details about this expense"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5, mr: 1 }}>
                          <DescriptionIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Provide a detailed description of the expense"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || !category || !amount}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
                    >
                      Record Expense
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Right side - Recent Expenses and Summary */}
      <Grid item xs={12} md={6}>
        <Card 
          variant="outlined" 
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CurrencyRupeeIcon sx={{ mr: 1 }} />
              Expense Summary
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Expenses Recorded
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                ₹{totalExpenses.toFixed(2)}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DateRangeIcon sx={{ mr: 1, fontSize: 20 }} />
              Expense History
            </Typography>

            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select value={filterCategory} label="Category" onChange={e => setFilterCategory(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {EXPENSE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField type="date" size="small" label="Start Date" InputLabelProps={{ shrink: true }} value={startDateFilter} onChange={e => setStartDateFilter(e.target.value)} />
              <TextField type="date" size="small" label="End Date" InputLabelProps={{ shrink: true }} value={endDateFilter} onChange={e => setEndDateFilter(e.target.value)} />
              <TextField type="number" size="small" label="Min ₹" sx={{ width: 80 }} value={minAmountFilter} onChange={e => setMinAmountFilter(e.target.value)} />
              <TextField type="number" size="small" label="Max ₹" sx={{ width: 80 }} value={maxAmountFilter} onChange={e => setMaxAmountFilter(e.target.value)} />
            </Box>
            
            {fetchingExpenses ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : filteredExpenses.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                px: 2, 
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  No expenses found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust your filters or add a new expense
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflowY: 'auto' }}>
                {filteredExpenses.map((expense) => (
                  <ListItem
                    key={expense.id}
                    alignItems="flex-start"
                    sx={{
                      px: 0,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Chip
                        size="small"
                        label={`₹${expense.amount.toFixed(2)}`}
                        color="primary"
                        variant="outlined"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={expense.category}
                      secondary={
                        <Box>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ 
                              display: 'block',
                              color: 'text.primary',
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            {expense.description}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatDate(expense.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}